<template>
  <div>
    <v-row align="center" no-gutters class="ml-0 mt-3">
      <v-col cols="12">
        <div :id="idMapa" class="mapa-leaflet"></div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import L from 'leaflet';
  import wms from 'leaflet.wms';
  import MapboxConfig from '@/config/MapboxConfig';
  import mapbox from '@/config/Mapbox';
  import GeoServerService from '@/services/GeoServerService';

  const mapboxConfig = new MapboxConfig(mapbox.access_token, mapbox.style);

  export default {
    name: 'MapaLeafletGeoserver',

    props: {
      idMapa: {
        type: String,
        required: true
      },

      zoomInicial: {
        type: Number,
        default: 4
      },

      centroInicial: {
        type: Array,
        required: true
      },

      bases: {
        type: Array,
        default: () => []
      },

      filtroCql: {
        type: String,
        default: null
      },

      filtroOgc: {
        type: String,
        default: null
      },

      obterNomeLayer: {
        type: Function,
        required: true
      },

      obterRotuloLayer: {
        type: Function,
        required: true
      },

      obterFiltroLayer: {
        type: Function,
        default: null
      },

      propriedadesIgnoradas: {
        type: Array,
        default: () => ['GEOM', 'CONTADOR', 'COMPANY', 'COMPANY_ID', 'MES_DADOS', 'BDGD_VERSAO', 'BDGD_VERSAO_ID']
      },

      ativarLayersAutomaticamente: {
        type: Boolean,
        default: false
      },

      layersAtivasInicialmente: {
        type: Array,
        default: () => []
      },

      aplicarZoomAutomatico: {
        type: Boolean,
        default: true
      },

      zoomMaximo: {
        type: Number,
        default: 16
      }
    },

    data() {
      return {
        mapa: null,
        controleLayers: null,
        layers: {},
        tentativasZoom: 0,
        maxTentativasZoom: 10
      };
    },

    mounted() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.inicializarMapa();
          this.limparLayers();
          this.adicionarLayers();
          if (this.mapa) {
            this.mapa.invalidateSize();
          }
        }, 80);
      });
    },

    methods: {
      inicializarMapa() {
        this.mapa = L.map(this.idMapa, {
          fullscreenControl: true,
          loadingControl: true,
          layers: [mapboxConfig.getDefaultLayer()],
          zoom: this.zoomInicial
        });

        this.mapa.setView(this.centroInicial, this.zoomInicial);

        this.controleLayers = L.control.layers(mapboxConfig.getBaseLayers());

        this.controleLayers.addTo(this.mapa);
      },

      limparLayers() {
        Object.values(this.layers).forEach((layer) => this.controleLayers.removeLayer(layer));
        this.layers = {};
        this.tentativasZoom = 0;
      },

      adicionarLayers() {
        this.bases.forEach((base) => this.adicionarLayer(base));

        if (this.aplicarZoomAutomatico && this.layersAtivasInicialmente.length > 0) {
          this.aplicarZoomNasEntidades();
        }
      },

      obterFiltroParaBase(base) {
        // Se existe a função obterFiltroLayer, usa ela para determinar o filtro específico
        if (this.obterFiltroLayer) {
          const filtroEspecifico = this.obterFiltroLayer(base);

          // Se retornar null, significa que não deve aplicar filtro nessa layer
          if (filtroEspecifico === null) {
            return { cql_filter: null, filter: null };
          }

          // Se retornou um filtro específico, usa ele
          if (filtroEspecifico) {
            // Detecta se é CQL ou OGC baseado no conteúdo
            const isCql = !filtroEspecifico.includes('<') && !filtroEspecifico.includes('>');
            return isCql ? { cql_filter: filtroEspecifico, filter: null } : { cql_filter: null, filter: filtroEspecifico };
          }
        }

        // Comportamento padrão: usa os filtros globais
        return this.filtroCql ? { cql_filter: this.filtroCql, filter: null } : { cql_filter: null, filter: this.filtroOgc };
      },
      adicionarLayer(base) {
        const geoserverBaseUrl = GeoServerService.getGeoServerBaseUrl();
        const baseUrl = GeoServerService.getWmsBaseUrl();
        const workspace = GeoServerService.getWorkspaceDefault();

        const FonteWms = wms.Source.extend({
          showFeatureInfo: (latlng, info) => {
            if (!info || info.includes('no features')) return;

            const conteudo = info
              .split('\n')
              .filter((_, i, arr) => ![0, 1, arr.length - 1, arr.length - 2].includes(i))
              .map((linha) => linha.split('='))
              .filter(([chave, valor]) => valor && valor !== 'null' && !this.propriedadesIgnoradas.includes(chave.toUpperCase()))
              .map(([chave, valor]) => `<b>${chave}</b>: ${valor}`)
              .join('<br>');

            L.popup().setLatLng(latlng).setContent(`<div class="popup">${conteudo}</div>`).openOn(this.mapa);
          }
        });

        // Obtém o filtro específico para esta base
        const { cql_filter, filter } = this.obterFiltroParaBase(base);

        const configuracao = {
          format: 'image/png',
          transparent: true,
          identify: true
        };

        // Adiciona o filtro apenas se não for null
        if (cql_filter !== null && cql_filter !== undefined) {
          configuracao.cql_filter = cql_filter;
        } else if (filter !== null && filter !== undefined) {
          configuracao.filter = filter;
        }

        const fonte = new FonteWms(`${geoserverBaseUrl}/${baseUrl}`, configuracao);

        const chaveLayer = this.obterNomeLayer(base);
        const layer = fonte.getLayer(`${workspace}:${chaveLayer}`);

        this.layers[chaveLayer] = layer;
        this.controleLayers.addOverlay(layer, this.obterRotuloLayer(base));

        const deveAtivar = this.ativarLayersAutomaticamente || this.layersAtivasInicialmente.includes(chaveLayer);

        if (deveAtivar) {
          this.mapa.addLayer(layer);
        }
      },

      async aplicarZoomNasEntidades() {
        this.tentarZoomViaGetFeatureInfo();
      },

      async tentarZoomViaGetFeatureInfo() {
        if (this.tentativasZoom >= this.maxTentativasZoom) {
          console.warn('Número máximo de tentativas de zoom atingido');
          return;
        }

        this.tentativasZoom++;

        const geoserverBaseUrl = GeoServerService.getGeoServerBaseUrl();
        const workspace = GeoServerService.getWorkspaceDefault();
        const layerParaZoom = this.layersAtivasInicialmente[0];

        if (!layerParaZoom) {
          console.warn('Nenhuma layer ativa para aplicar zoom');
          return;
        }

        try {
          const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: `${workspace}:${layerParaZoom}`,
            outputFormat: 'application/json',
            maxFeatures: 100
          });

          // Busca a base correspondente para obter o filtro correto
          const baseParaZoom = this.bases.find((base) => this.obterNomeLayer(base) === layerParaZoom);
          if (baseParaZoom) {
            const { cql_filter, filter } = this.obterFiltroParaBase(baseParaZoom);
            if (cql_filter) {
              params.append('cql_filter', cql_filter);
            } else if (filter) {
              params.append('filter', filter);
            }
          }

          const url = `${geoserverBaseUrl}/wfs?${params.toString()}`;
          const response = await fetch(url);
          const data = await response.json();

          if (data.features && data.features.length > 0) {
            let todasCoordenadas = [];

            data.features.forEach((feature) => {
              if (feature.geometry && feature.geometry.coordinates) {
                const coords = this.extrairCoordenadas(feature.geometry);
                todasCoordenadas = todasCoordenadas.concat(coords);
              }
            });

            if (todasCoordenadas.length > 0) {
              const bounds = L.latLngBounds(todasCoordenadas);
              this.mapa.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: this.zoomMaximo
              });
            }
          }
        } catch (error) {
          console.warn('Erro ao buscar features para zoom:', error);
        }
      },

      extrairCoordenadas(geometry) {
        const coords = [];

        const processarCoordenadas = (coordArray) => {
          if (Array.isArray(coordArray[0])) {
            coordArray.forEach(processarCoordenadas);
          } else {
            coords.push([coordArray[1], coordArray[0]]);
          }
        };

        if (geometry.type === 'Point') {
          coords.push([geometry.coordinates[1], geometry.coordinates[0]]);
        } else if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
          processarCoordenadas(geometry.coordinates);
        } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
          geometry.coordinates.forEach(processarCoordenadas);
        } else if (geometry.type === 'MultiPolygon') {
          geometry.coordinates.forEach((polygon) => {
            polygon.forEach(processarCoordenadas);
          });
        }

        return coords;
      }
    },

    watch: {
      bases: {
        immediate: false,
        handler() {
          if (!this.mapa) return;
          this.limparLayers();
          this.adicionarLayers();
        }
      },

      filtroCql() {
        if (!this.mapa) return;
        this.limparLayers();
        this.adicionarLayers();
      },

      filtroOgc() {
        if (!this.mapa) return;
        this.limparLayers();
        this.adicionarLayers();
      }
    }
  };
</script>

<style scoped>
  .mapa-leaflet {
    height: 100%;
    min-height: 400px;
    z-index: 0;
  }

  .popup {
    max-height: 200px;
    overflow-y: auto;
  }
</style>
